import React from 'react';
import { Chip, Grid, Stack, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { usePagedList } from '../../../hooks/use-paged-list';
import { formatDate } from '../../../utils/date';
import { useNewsSearchController } from './context/news-search-controller';
import { useNewsSearchState } from './context/news-search-context';
import NewsAppService from '../../../application/platform/apm/news/news-app-service';
import { INewsListResponse } from '../../../model/apm/news/response/news-list-response';
import { FilterInput, FilterSelect } from '../../../components/filter-fields';
import { PUBLICATION_STATUS_OPTIONS, booleanFilterValue, lookupOptions } from '../../../utils/filter-options';
import { PagedGrid } from '../../../components/paged-grid';
import { DeleteAction, EditAction, PublicationAction } from '../../../components/row-actions';
import { NewsDrawer } from './components/news-drawer';

function NewsStatus({ news }: { news: INewsListResponse }) {
    const scheduled = news.isPublished && news.publishDate && dayjs(news.publishDate).isAfter(dayjs(), 'day');
    return (
        <Stack direction="row" spacing={0.75}>
            {scheduled
                ? <Chip size="small" color="info" label={`Agendada ${formatDate(news.publishDate)}`} />
                : <Chip size="small" color={news.isPublished ? 'success' : 'default'} label={news.isPublished ? 'Publicada' : 'Rascunho'} />}
            {news.isHighlighted && (
                <Tooltip title="Em destaque">
                    <StarIcon fontSize="small" color="warning" sx={{ alignSelf: 'center' }} />
                </Tooltip>
            )}
        </Stack>
    );
}

export function NewsSearchComponent() {
    const ctrl = useNewsSearchController();
    const { parameters, searchTrigger, categories, drawer } = useNewsSearchState();
    const list = usePagedList(NewsAppService.list, parameters, searchTrigger, 'Não foi possível carregar as notícias');

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'title', headerName: 'Título', flex: 1, minWidth: 240 },
        { field: 'category', headerName: 'Categoria', width: 180 },
        { field: 'date', headerName: 'Data', width: 110, valueFormatter: (params) => formatDate(params.value) },
        { field: 'isPublished', headerName: 'Situação', width: 200, sortable: false, renderCell: (params) => <NewsStatus news={params.row} /> },
        { field: 'views', headerName: 'Leituras', width: 100, type: 'number' },
        {
            field: 'actions', headerName: 'Ações', width: 140, sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <PublicationAction isPublished={params.row.isPublished} onClick={() => ctrl.handleTogglePublication(params.row)} />
                    <EditAction onClick={() => ctrl.openDrawer(params.row)} />
                    <DeleteAction onClick={() => ctrl.handleDelete(params.row)} />
                </React.Fragment>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Notícias"
                    subtitle="Cadastre, publique e destaque as notícias do portal"
                    breadcrumbs={[{ label: 'Notícias' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch}>
                    <Grid item xs={12} md={6}>
                        <FilterInput label="Título" name="input-term" value={parameters.term} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FilterSelect label="Categoria" name="select-category" value={parameters.categoryId} options={lookupOptions(categories)} emptyLabel="Todas" onChange={ctrl.handleOnChange} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FilterSelect
                            label="Situação"
                            name="select-status"
                            value={booleanFilterValue(parameters.isPublished)}
                            options={PUBLICATION_STATUS_OPTIONS}
                            emptyLabel="Todas"
                            onChange={ctrl.handleOnChange}
                        />
                    </Grid>
                </FilterPanel>
            </Grid>
            <Grid item xs={12}>
                <PagedGrid
                    columns={columns}
                    list={list}
                    parameters={parameters}
                    onPaginationChange={ctrl.handlePaginationChange}
                    addLabel="Nova Notícia"
                    onAdd={() => ctrl.openDrawer()}
                />
            </Grid>

            <NewsDrawer
                open={drawer.open}
                initial={drawer.news}
                categories={categories}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.handleSave}
            />
        </React.Fragment>
    );
}
